import React, { useEffect } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import styles from "./AddPhoneNumberWabaModal.module.scss";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { useTranslation } from "react-i18next";
import { PhoneInput } from "@whatslly/ui-components/v2/BaseElements/PhoneInput";
import { action, computed, makeObservable, observable } from "mobx";
import { countryCodes } from "@whatslly/commons/phoneUtils/fullCountryCodesData";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";

class AddPhoneNumberWabaModalStore {
  countryCode = "";
  phoneNumber = "";
  verifiedName = "";
  wabaOption: { label: string; value: string } | undefined = undefined;
  constructor() {
    makeObservable<AddPhoneNumberWabaModalStore, "isValidPhone">(this, {
      wabaOption: observable,
      countryCode: observable,
      phoneNumber: observable,
      verifiedName: observable,
      isValidPhone: computed,
      setCountryCode: action,
      setPhoneNumber: action,
      setVerifiedName: action,
      isValidInput: computed,
      submitData: computed,
      selectedWaba: computed,
      setWabaOption: action,
    });
  }

  setVerifiedName(verifiedName: string) {
    this.verifiedName = verifiedName;
  }

  setCountryCode(code: string) {
    this.countryCode = stripNumberStringToOnlyNumbers(code);
  }

  setPhoneNumber(number: string) {
    this.phoneNumber = stripNumberStringToOnlyNumbers(number);
  }

  private get isValidPhone(): boolean {
    const thisCountryCodeAsNumber = Number(this.countryCode);
    const currentCountryData = countryCodes.filter((c) => c.code === thisCountryCodeAsNumber);
    const phone = `${this.countryCode}${this.phoneNumber}`;
    const phoneFitToRegex = currentCountryData.find((c) => new RegExp(c.regex).test(phone));
    return this.phoneNumber.length >= 8 && this.phoneNumber.length <= 15 && Boolean(phoneFitToRegex);
  }

  get isValidInput(): boolean {
    return (
      Boolean(this.wabaOption) &&
      this.isValidPhone &&
      !Number.isNaN(Number(this.phoneNumber)) &&
      !Number.isNaN(Number(this.countryCode)) &&
      Boolean(this.verifiedName)
    );
  }

  get submitData() {
    return {
      verifiedName: this.verifiedName,
      phoneNumber: Number(this.phoneNumber),
      countryCode: Number(this.countryCode),
      wabaId: this.wabaOption!.value,
    };
  }

  setWabaOption(value: { label: string; value: string }) {
    this.wabaOption = value;
  }

  get selectedWaba() {
    return this.wabaOption;
  }
}

type WabaOption = { label: string; value: string };

interface AddPhoneNumberWabaModalProps extends IModalInstance<{ verifiedName: string; phoneNumber: number; countryCode: number; wabaId: string }> {
  wabaSelectOptions: WabaOption[];
}

export const AddPhoneNumberWabaModal: React.FC<AddPhoneNumberWabaModalProps> = observer(({ closeModal, wabaSelectOptions }) => {
  const { t, i18n } = useTranslation();
  const localStore = useLocalStoreWithInjectedDependencies(AddPhoneNumberWabaModalStore);

  useEffect(() => {
    localStore.setWabaOption(wabaSelectOptions[0]);
  }, [localStore, wabaSelectOptions]);

  return (
    <BaseModal
      header={<ModalHeader closeModal={() => closeModal()}>{t("whatsapp_business_api_settings_page.addPhoneNumberModal.title")}</ModalHeader>}
      footer={
        <ModalButtons
          isSubmitDisabled={!localStore.isValidInput}
          testId="modal-buttons-submit-button"
          onCancel={() => closeModal()}
          onSubmit={() => closeModal(localStore.submitData)}
        />
      }
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={() => closeModal()}
      className={styles.modal}
    >
      <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.cols.wabaName")}</div>
      <Select
        data-testid="add-waba-phone-account-select"
        placeholder={t("whatsapp_business_api_settings_page.addPhoneNumberModal.select_account_placeholder")}
        options={wabaSelectOptions}
        value={localStore.selectedWaba}
        onChange={(item: { label: string; value: string } | null) => item && localStore.setWabaOption(item)}
      />
      <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.addPhoneNumberModal.name_field_title")}</div>
      <div className={styles.fieldSubTitle}>
        {`${t("whatsapp_business_api_settings_page.addPhoneNumberModal.name_field_sub_title")} `}
        <a href={"https://www.facebook.com/business/help/757569725593362"} target="_blank">
          {t("whatsapp_business_api_settings_page.addPhoneNumberModal.name_field_sub_title_link")}
        </a>
      </div>
      <TextInput
        data-testid="add-waba-phone-verified-name-input"
        paddingInline={`none`}
        value={localStore.verifiedName}
        onChange={(event) => localStore.setVerifiedName(event.target.value)}
        onClear={() => localStore.setVerifiedName("")}
      />
      <div className={styles.fieldTitle}>{t("whatsapp_business_api_settings_page.addPhoneNumberModal.phone_number_field_title")}</div>
      <div className={styles.fieldSubTitle}>
        {`${t("whatsapp_business_api_settings_page.addPhoneNumberModal.phone_number_field_sub_title")} `}
        <a
          href={"https://developers.facebook.com/docs/whatsapp/cloud-api/get-started/migrate-existing-whatsapp-number-to-a-business-account"}
          target="_blank"
        >
          {t("whatsapp_business_api_settings_page.addPhoneNumberModal.phone_number_field_sub_title_link")}
        </a>
      </div>
      <PhoneInput
        testid="add-waba-phone-number-input"
        disableBackgroundColor={true}
        countryCode={i18n.language === "pt" ? "55" : ""}
        phoneNumberWithOutCountryCode={localStore.phoneNumber}
        onPhoneChange={(phoneNumberWithOutCountryCode) => localStore.setPhoneNumber(phoneNumberWithOutCountryCode)}
        onCountryCodeChange={(code) => localStore.setCountryCode(code)}
      />
    </BaseModal>
  );
});
